import { Component, OnInit, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { IValueChangeEvent } from '../viega-slider/viega-slider.component';
import { ValueAccessor } from '../ValueAccessor';
import { IRangeInput } from '../IRangeInput';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

export interface IMeasureInputValueChangeEvent {
    newValue: number;
}

@Component({
    selector: 'measure-input',
    templateUrl: './measure-input.component.html',
    styleUrls: ['./measure-input.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MeasureInputComponent),
        multi: true
    }],
    inputs: ['value']
})
export class MeasureInputComponent extends ValueAccessor<number> implements IRangeInput {
    private static readonly KeyCodeUpArrow = 38;
    private static readonly KeyCodeDownArrow = 40;

    public internalValue: string;
    public isValid: boolean = true;

    @ViewChild('input')
    public inputElement: ElementRef;

    public get isDisabled(): boolean {
        return this.disabled;
    }
    @Input()
    public set isDisabled(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    @Input()
    public inputId: number;

    private _min: number = 0;
    public get min(): number {
        return this._min;
    }
    @Input()
    public set min(value: number) {
        if (this.value >= this.min && this.value < value)
            this.setValue(this._min);

        this._min = value;
        this.isValid = this.validateValue(this.internalValue);
    }

    private _max: number = 100;
    public get max(): number {
        return this._max;
    }
    @Input()
    public set max(value: number) {
        if (this.value <= this._max && this.value > value)
            this.setValue(this._max);

        this._max = value;
        this.isValid = this.validateValue(this.internalValue);
    }

    @Input()
    public increment: number = 1;

    @Input()
    public isSliderEnabled: boolean = true;

    @Input()
    public measureUnit: string = "cm";

    @Input()
    public inputWidth: string = "1.7em";

    @Input()
    public allowInputOverMinMax: boolean = false;

    @Input()
    public allowFloat: boolean = false;

    @Input()
    /**
     * Whether this component shall update ngModel when invalid values have been entered or not.
     */
    public allowInvalid: boolean = false;

    @Output()
    public valueChange = new EventEmitter<IMeasureInputValueChangeEvent>();

    @Output()
    public keyUp = new EventEmitter<KeyboardEvent>();

    @Output()
    public keyDown = new EventEmitter<KeyboardEvent>();

    private input_keyUp(event: KeyboardEvent) {
        this.keyUp.emit(event);
    }

    public internalValueChanged() {
        if (typeof this.internalValue === 'number')
            this.internalValue = (<number>this.internalValue).toString();

        if (this.allowInvalid || this.validateValue(this.internalValue)) {
            const newValue = this.allowFloat ? parseFloat(this.internalValue) : parseInt(this.internalValue, 10);
            this.setValue(newValue);
            this.isValid = true;
        } else {
            this.isValid = false;
        }
    }

    private input_keyDown(event: KeyboardEvent) {
        this.keyDown.emit(event);

        let step = 0;
        if (event.keyCode === MeasureInputComponent.KeyCodeUpArrow)
            step = this.increment;
        else if (event.keyCode === MeasureInputComponent.KeyCodeDownArrow)
            step = -this.increment;

        if (step !== 0) {
            let value = this.value;
            value += step;

            if (this.allowInvalid || this.validateValue(value.toString()))
                this.setValue(value);
        }
    }

    private validateValue(value: string): boolean {
        if (value === '' || value === undefined || value === null)
            return false;

        const valueNumber = this.allowFloat ? parseFloat(value) : parseInt(value, 10);
        const validNumber = !isNaN(valueNumber) && value.trim() === valueNumber.toString();
        const inRange = validNumber && valueNumber <= this.max && valueNumber >= this.min;

        return (validNumber && (this.allowInputOverMinMax || inRange));
    }

    private slider_valueChange(event: IValueChangeEvent) {
        this.setValue(Math.round(event.newValue));
    }

    private setValue(newValue: number) {
        this.value = newValue;
        this.valueChange.emit({ newValue });
    }

    public focus() {
        this.inputElement.nativeElement.focus();
    }

    public ngModelValueUpdated(value: number) {
        this.internalValue = value !== undefined && value !== null ? value.toString() : <null | undefined>value;
        this.isValid = this.validateValue(this.internalValue);
    }
}



// WEBPACK FOOTER //
// ./src/viega-common/components/measure-input/measure-input.component.ts